import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  X,
  Mail,
  Phone,
  MapPin,
  Building,
  Briefcase,
  CheckCircle,
  Calendar,
  Edit,
  Gift,
  Coins,
  Trash2,
} from 'lucide-react';
import { Button } from '../../common/Button';
import { Profile } from '../../../types/profile';
import FullBlockingModal from '../../common/FullBlockingModal';
import ProfileAvatar from '../../common/ProfileAvatar';
import { formatDateLocalized } from '../../../utils/dateUtils';
import i18n from '../../../i18n';
import { useNavigate } from 'react-router-dom';
import GiftCreditsModal from './GiftCreditsModal';
import DeleteUserModal from './DeleteUserModal';
import { useAdminStore } from '../../../stores/adminStore';
import { toast } from 'react-toastify';
import { COMPANY_TYPES } from '../../../types/profile';
import type { AdminUserActivity } from '../../../types/admin_user_activity';
import { adminUserService } from '../../../services/adminUserService';
import AdminUserActivitySection from '../user/AdminUserActivitySection';

const EMPTY_ACTIVITY: AdminUserActivity = { quotes: [], projects: [], jobs: [] };

interface ProfileDetailsModalProps {
  onClose: () => void;
  profile: Profile | null;
  onClick?: () => void;
}

const AdminProfileDetailsModal: React.FC<ProfileDetailsModalProps> = ({
  onClose,
  profile,
  onClick,
}) => {
  const { t } = useTranslation(['admin', 'common']);
  const navigate = useNavigate();
  const [showGiftCredits, setShowGiftCredits] = useState(false);
  const [showDeleteUser, setShowDeleteUser] = useState(false);
  const [obnlReviewStatus, setObnlReviewStatus] = useState(profile?.obnl_review_status);
  const [updatingObnlStatus, setUpdatingObnlStatus] = useState(false);
  const [activity, setActivity] = useState<AdminUserActivity>(EMPTY_ACTIVITY);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityError, setActivityError] = useState<string | null>(null);
  const [creditBalance, setCreditBalance] = useState<number | null>(null);
  const { updateObnlReviewStatus } = useAdminStore();
  const profileId = profile?.id;
  const profileRole = profile?.role;
  const profileObnlReviewStatus = profile?.obnl_review_status;

  useEffect(() => {
    setObnlReviewStatus(profileObnlReviewStatus);
  }, [profileObnlReviewStatus]);

  useEffect(() => {
    if (!profileId || profileRole === 'administrator') {
      setActivity(EMPTY_ACTIVITY);
      setActivityLoading(false);
      setActivityError(null);
      return;
    }

    let active = true;
    setActivity(EMPTY_ACTIVITY);
    setActivityLoading(true);
    setActivityError(null);

    void adminUserService
      .fetchActivity(profileId)
      .then(nextActivity => {
        if (active) setActivity(nextActivity);
      })
      .catch(() => {
        if (active) {
          setActivityError('userActivity.loadError');
        }
      })
      .finally(() => {
        if (active) setActivityLoading(false);
      });

    return () => {
      active = false;
    };
  }, [profileId, profileRole]);

  useEffect(() => {
    if (!profileId || profileRole === 'administrator') {
      setCreditBalance(null);
      return;
    }

    let active = true;
    setCreditBalance(null);

    void adminUserService
      .fetchCreditBalance(profileId)
      .then(balance => {
        if (active) setCreditBalance(balance);
      })
      .catch(() => {
        // Balance is non-critical context; leave it hidden on failure.
      });

    return () => {
      active = false;
    };
  }, [profileId, profileRole]);

  if (!profile) return null;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-700';
      case 'suspended':
        return 'bg-orange-100 text-orange-700';
      case 'banned':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const goToFreelancerProfileDetails = (profile: Profile) => {
    if (profile.role === 'freelancer') {
      navigate(`/admin/freelancers/${profile.id}`);
    }
  };

  const goToCreditTransactions = (profile: Profile) => {
    navigate('/admin/credit-transactions', {
      state: {
        userId: profile.id,
        userName: `${profile.first_name ?? ''} ${profile.last_name ?? ''}`.trim(),
      },
    });
  };

  const handleObnlReviewUpdate = async (nextStatus: 'approved' | 'rejected') => {
    try {
      setUpdatingObnlStatus(true);
      await updateObnlReviewStatus(profile.id, nextStatus);
      setObnlReviewStatus(nextStatus);
      toast.success(
        t(`obnlReview.${nextStatus}Success`, {
          ns: 'admin',
          defaultValue:
            nextStatus === 'approved'
              ? 'OBNL status approved successfully'
              : 'OBNL status rejected successfully',
        })
      );
    } catch (error) {
      console.error(error);
      toast.error(
        t('obnlReview.error', {
          ns: 'admin',
          defaultValue: 'Could not update the OBNL review status.',
        })
      );
    } finally {
      setUpdatingObnlStatus(false);
    }
  };

  return (
    <FullBlockingModal innerClassName="md:max-w-[70rem]">
      <div className="border-2xl overflow-hidden flex flex-col">
        <div className="flex justify-between items-start p-6 border-b border-gray-100">
          <div className="flex gap-4">
            <div className="h-16 w-16 rounded-full ring-2 ring-white overflow-hidden shadow-sm">
              <ProfileAvatar
                profile_id={profile.id}
                first_name={profile.first_name || ''}
                avatar_url={profile.avatar_url}
                className="h-16 w-16"
              />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                {profile.first_name} {profile.last_name}
              </h2>
              <div className="flex gap-2 mt-2">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize">
                  {t(`role.${profile.role}`, { ns: 'common' })}
                </span>
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getStatusColor(profile.status)}`}
                >
                  {t(`user_status.${profile.status}`, { ns: 'common' })}
                </span>
              </div>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500 transition-colors">
            <X size={24} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto custom-scrollbar space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <InfoItem
              icon={<MapPin size={18} />}
              label={t('city')}
              value={profile.city?.name || '-'}
            />
            <InfoItem icon={<Phone size={18} />} label={t('phone')} value={profile.phone || '-'} />
            <InfoItem icon={<Mail size={18} />} label={t('email')} value={profile.email || '-'} />
            <InfoItem
              icon={<Calendar size={18} />}
              label={t('table_fields.created_at', { ns: 'admin', defaultValue: 'Inscrit le' })}
              value={formatDateLocalized(profile.created_at, i18n.language, {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              })}
            />

            {profile.role !== 'administrator' && creditBalance !== null && (
              <button
                type="button"
                onClick={() => goToCreditTransactions(profile)}
                className="flex items-start gap-3 text-left group focus:outline-none"
                title={t('creditTransactions.viewUserTransactions', {
                  ns: 'admin',
                  defaultValue: 'Voir les transactions de crédits',
                })}
              >
                <div className="text-gray-400 mt-0.5 group-hover:text-primary transition-colors">
                  <Coins size={18} />
                </div>
                <div>
                  <p className="text-xs text-gray-500">
                    {t('creditTransactions.availableCredits', {
                      ns: 'admin',
                      defaultValue: 'Crédits disponibles',
                    })}
                  </p>
                  <p className="text-sm font-medium text-primary underline-offset-2 group-hover:underline">
                    {creditBalance}
                  </p>
                </div>
              </button>
            )}
          </div>

          <hr className="border-gray-100" />

          <div className="space-y-4">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-500">
              {t('informations', { defaultValue: 'Informations' })}
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {profile.role === 'recruiter' ? (
                <>
                  <InfoItem
                    icon={<Building size={18} />}
                    label={t('table_fields.company', { ns: 'admin', defaultValue: 'Entreprise' })}
                    value={profile.company_name}
                  />
                  <InfoItem
                    icon={<Building size={18} />}
                    label={t('table_fields.company_type', { ns: 'admin' })}
                    value={
                      profile.company_type
                        ? t(`companyType.${profile.company_type}`, {
                            ns: 'common',
                            defaultValue: profile.company_type,
                          })
                        : '-'
                    }
                  />

                  <InfoItem
                    icon={<Briefcase size={18} />}
                    label={t('table_fields.job_title', {
                      ns: 'admin',
                      defaultValue: 'Intitulé du poste',
                    })}
                    value={profile.job_title}
                  />
                  {profile.company_type === COMPANY_TYPES.ORGANIZATION && (
                    <>
                      <InfoItem
                        icon={<Building size={18} />}
                        label={t('table_fields.organization_website', {
                          ns: 'admin',
                          defaultValue: 'Organization website',
                        })}
                        value={profile.organization_website}
                      />
                      <InfoItem
                        icon={<Building size={18} />}
                        label={t('table_fields.organization_number', {
                          ns: 'admin',
                          defaultValue: 'Organization number',
                        })}
                        value={profile.organization_number}
                      />
                      <InfoItem
                        icon={<CheckCircle size={18} />}
                        label={t('table_fields.obnl_review_status', {
                          ns: 'admin',
                          defaultValue: 'OBNL review status',
                        })}
                        value={
                          obnlReviewStatus
                            ? t(`obnl_review_status.${obnlReviewStatus}`, {
                                ns: 'common',
                                defaultValue: obnlReviewStatus,
                              })
                            : '-'
                        }
                      />
                    </>
                  )}

                  <InfoItem
                    icon={<Briefcase size={18} />}
                    label={t('navigation.industries', {
                      ns: 'admin',
                      defaultValue: "Domaines d'activité",
                    })}
                    value={profile.industries
                      ?.map(ind =>
                        t(`industries.${ind.slug}`, { ns: 'common', defaultValue: ind.label })
                      )
                      .join(', ')}
                  />
                </>
              ) : profile.role === 'freelancer' ? (
                <div></div>
              ) : null}
            </div>

            {profile.bio && (
              <InfoItem
                icon={<Edit size={18} />}
                label={t('description', { ns: 'projects', defaultValue: 'Description' })}
                value={profile.bio}
              />
            )}

            {profile.role !== 'administrator' && (
              <>
                <hr className="border-gray-100" />
                <AdminUserActivitySection
                  role={profile.role}
                  profileId={profile.id}
                  activity={activity}
                  loading={activityLoading}
                  error={activityError}
                />
              </>
            )}
          </div>
        </div>

        <hr className="border-gray-300" />

        <div className="m-4 flex flex-row flex-wrap gap-3 justify-center">
          {profile.role !== 'administrator' && profile.role === 'freelancer' && (
            <Button
              variant="transparent-with-border"
              onClick={() => {
                goToFreelancerProfileDetails(profile);
              }}
            >
              {t('buttons.viewProfile', { ns: 'common', defaultValue: 'Voir profil complet' })}
            </Button>
          )}

          {profile.role !== 'administrator' && (
            <Button variant="transparent-with-border" onClick={() => setShowGiftCredits(true)}>
              <Gift size={16} className="mr-2" />
              {t('creditGift.giftCredits', { ns: 'admin', defaultValue: 'Gift credits' })}
            </Button>
          )}

          {profile.role === 'recruiter' && profile.company_type === COMPANY_TYPES.ORGANIZATION && (
            <>
              <Button
                variant="transparent-with-border"
                onClick={() => handleObnlReviewUpdate('approved')}
                disabled={updatingObnlStatus || obnlReviewStatus === 'approved'}
              >
                {t('obnlReview.approve', {
                  ns: 'admin',
                  defaultValue: 'Approve OBNL',
                })}
              </Button>
              <Button
                variant="transparent-with-border"
                onClick={() => handleObnlReviewUpdate('rejected')}
                disabled={updatingObnlStatus || obnlReviewStatus === 'rejected'}
              >
                {t('obnlReview.reject', {
                  ns: 'admin',
                  defaultValue: 'Reject OBNL',
                })}
              </Button>
            </>
          )}

          <Button variant="primary" onClick={onClick}>
            <CheckCircle size={16} className="mr-2" />
            {t('update_status_button', {
              ns: 'common',
              defaultValue: 'Modifier le statut',
            })}
          </Button>

          {profile.role !== 'administrator' && (
            <Button
              variant="transparent-with-border"
              onClick={() => setShowDeleteUser(true)}
              className="!border-red-600 !text-red-600 hover:!bg-red-50"
            >
              <Trash2 size={16} className="mr-2" />
              {t('deleteUser.deleteButton', { ns: 'admin', defaultValue: 'Delete user' })}
            </Button>
          )}
        </div>
      </div>

      {showGiftCredits && profile && (
        <GiftCreditsModal profile={profile} onClose={() => setShowGiftCredits(false)} />
      )}

      {showDeleteUser && profile && (
        <DeleteUserModal
          profile={profile}
          onClose={() => setShowDeleteUser(false)}
          onSuccess={onClose}
        />
      )}
    </FullBlockingModal>
  );
};

export const InfoItem = ({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value?: string;
}) => (
  <div className="flex items-start gap-3">
    <div className="text-gray-400 mt-0.5">{icon}</div>
    <div>
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-sm font-medium text-gray-900">{value || '-'}</p>
    </div>
  </div>
);

export default AdminProfileDetailsModal;
